import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { AddChip } from '@/components/ui/add-chip'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, deleteJSON, getJSON, postJSON } from '@/lib/api'
import type { MemberPortalOrganization } from './MemberSignIn'

type OrganizationRole = {
  id: number
  person_id: number
  person_name: string
  email: string | null
  roles: string[]
}

type EligiblePerson = { id: number; name: string }

export function MemberPortalOrganizationRolesPanel({
  organization,
}: {
  organization: MemberPortalOrganization
}) {
  const queryClient = useQueryClient()
  const [adding, setAdding] = React.useState(false)
  const [personId, setPersonId] = React.useState('')
  const [role, setRole] = React.useState('Roster manager')
  const rolesQuery = useQuery<{ roles: OrganizationRole[]; people: EligiblePerson[] }>({
    queryKey: ['member-portal-organization-roles', organization.id],
    queryFn: () => getJSON(`/api/v1/member_portal/organizations/${organization.id}/roles`),
  })
  const add = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/member_portal/organizations/${organization.id}/roles`, {
        role: { person_id: Number(personId), role },
      }),
    onSuccess: () => {
      setAdding(false)
      setPersonId('')
      queryClient.invalidateQueries({
        queryKey: ['member-portal-organization-roles', organization.id],
      })
    },
  })
  const remove = useMutation({
    mutationFn: ({ id, role, confirm }: { id: number; role: string; confirm?: boolean }) =>
      deleteJSON(`/api/v1/member_portal/organizations/${organization.id}/roles/${id}`, {
        role: { role },
        ...(confirm ? { confirm: true } : {}),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['member-portal-organization-roles', organization.id],
      }),
    onError: (error, variables) => {
      if (
        error instanceof ApiError &&
        typeof error.body === 'object' &&
        error.body !== null &&
        'confirmation_required' in error.body &&
        window.confirm(
          String((error.body as { message?: string }).message ?? 'Confirm removing your role?'),
        )
      ) {
        remove.mutate({ ...variables, confirm: true })
      }
    },
  })
  const people = rolesQuery.data?.people ?? []
  const assignments = rolesQuery.data?.roles ?? []

  return (
    <section className="border-t border-border px-4 py-4 sm:px-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-body font-medium">Roles</h2>
        {!adding && <AddChip onClick={() => setAdding(true)}>Add role</AddChip>}
      </div>
      {adding && (
        <RoleForm
          people={people}
          personId={personId}
          role={role}
          onPersonChange={setPersonId}
          onRoleChange={setRole}
          onGrant={() => add.mutate()}
          onCancel={() => setAdding(false)}
          pending={add.isPending}
        />
      )}
      {rolesQuery.isLoading ? (
        <p className="mt-3 text-caption text-muted-foreground" role="status">
          Loading roles...
        </p>
      ) : rolesQuery.isError ? (
        <p className="mt-3 text-caption text-destructive" role="alert">
          Unable to load roles.
        </p>
      ) : (
        <div className="mt-3 divide-y divide-border">
          {assignments.map((assignment) => (
            <RoleRow
              key={assignment.id}
              assignment={assignment}
              onRemove={(assignedRole) => remove.mutate({ id: assignment.id, role: assignedRole })}
            />
          ))}
        </div>
      )}
      <p className="mt-2 text-caption text-muted-foreground">
        Billing contacts see and pay invoices. Roster managers manage this page.
      </p>
    </section>
  )
}

function RoleForm({
  people,
  personId,
  role,
  onPersonChange,
  onRoleChange,
  onGrant,
  onCancel,
  pending,
}: {
  people: EligiblePerson[]
  personId: string
  role: string
  onPersonChange: (value: string) => void
  onRoleChange: (value: string) => void
  onGrant: () => void
  onCancel: () => void
  pending: boolean
}) {
  return (
    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="space-y-1">
        <Label htmlFor="role-person">Person</Label>
        <Select value={personId} onValueChange={onPersonChange}>
          <SelectTrigger id="role-person">
            <SelectValue placeholder="Select a person" />
          </SelectTrigger>
          <SelectContent>
            {people.map((person) => (
              <SelectItem key={person.id} value={String(person.id)}>
                {person.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="role-kind">Role</Label>
        <Select value={role} onValueChange={onRoleChange}>
          <SelectTrigger id="role-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Roster manager">Roster manager</SelectItem>
            <SelectItem value="Billing contact">Billing contact</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={onGrant} disabled={!personId || pending}>
          Grant
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function RoleRow({
  assignment,
  onRemove,
}: {
  assignment: OrganizationRole
  onRemove: (role: string) => void
}) {
  const assignedRole = assignment.roles[0]
  return (
    <div className="flex items-center gap-3 py-2">
      <Avatar className="size-8">
        <AvatarFallback>
          {assignment.person_name
            .split(' ')
            .map((part) => part[0])
            .join('')}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-sm">{assignment.person_name}</p>
        <p className="truncate text-caption text-muted-foreground">{assignment.email}</p>
      </div>
      <div className="flex shrink-0 gap-1">
        {assignment.roles.map((item) => (
          <Badge key={item} variant="neutral">
            {item}
          </Badge>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-label={`Revoke role from ${assignment.person_name}`}
        className="size-8 border-transparent bg-transparent px-0 text-muted-foreground shadow-none"
        onClick={() =>
          window.confirm(`Remove ${assignedRole} from ${assignment.person_name}?`) &&
          onRemove(assignedRole)
        }
      >
        <X className="size-4" aria-hidden="true" />
      </Button>
    </div>
  )
}
